import { useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { saveEmailIntegrationSettings } from '@/hooks/user';

import { Button } from '../ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';

// Email settings schema
const emailSchema = z.object({
  api_key: z.string().optional(),
  inbound_domain: z.string().min(1, 'Inbound domain is required').trim(),
  from_email: z
    .string()
    .min(1, 'From email is required')
    .email('Please enter a valid email address')
    .trim()
    .transform((val) => val.toLowerCase()),
  from_name: z.string().min(1, 'From name is required').trim(),
  reply_to_email: z
    .string()
    .min(1, 'Reply to email is required')
    .email('Please enter a valid email address')
    .trim()
    .transform((val) => val.toLowerCase()),
});

const Email = () => {
  const navigate = useNavigate();
  const { emailFeatureEnabled } = useUserStore(
    useShallow((state) => ({
      emailFeatureEnabled: state.emailFeatureEnabled,
    }))
  );

  useEffect(() => {
    if (emailFeatureEnabled === false) {
      navigate('/settings');
    }
  }, [emailFeatureEnabled, navigate]);

  if (!emailFeatureEnabled) {
    return null;
  }

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>Email</h3>
          <p className='text-sm text-muted-foreground'>
            Manage your Email connection via SendGrid
          </p>
        </div>
      </div>
      <Separator />
      <EmailConnection />
    </div>
  );
};

export default Email;

const EmailConnection = () => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const { user } = useUserStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );

  const sendgridDetails = user?.customer?.sendgrid_integration_details;
  const isConnected =
    sendgridDetails?.connected === true ||
    sendgridDetails?.connection_status === 'connected';

  const form = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      api_key: '',
      inbound_domain: '',
      from_email: '',
      from_name: '',
      reply_to_email: '',
    },
  });

  // Check email connection status and populate form
  useEffect(() => {
    // Don't show anything until user data is loaded
    if (!user) {
      setLoading(true);
      return;
    }

    setLoading(true);
    try {
      if (sendgridDetails) {
        // Pre-fill form inputs with existing values if available
        form.reset({
          api_key: '', // Never pre-fill API key for security
          inbound_domain: sendgridDetails.inbound_domain || '',
          from_email: sendgridDetails.from_email || '',
          from_name: sendgridDetails.from_name || '',
          reply_to_email: sendgridDetails.reply_to_email || '',
        });
      }
    } catch (error) {
      console.error('Failed to check Email status:', error);
    } finally {
      setLoading(false);
    }
  }, [user, sendgridDetails, form]);

  const onSubmit = async (data) => {
    // Validate API key requirement for new connections
    if (!isConnected && !data.api_key) {
      form.setError('api_key', {
        type: 'manual',
        message: 'API key is required for new connections',
      });
      toast.error('Please provide a SendGrid API key');
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      const payload = {
        inbound_domain: data.inbound_domain,
        from_email: data.from_email,
        from_name: data.from_name,
        reply_to_email: data.reply_to_email,
      };

      // Only include API key if provided (for updates, it's optional)
      if (data.api_key) {
        payload.api_key = data.api_key;
      }

      const response = await saveEmailIntegrationSettings(payload);

      // Check success conditions
      const outerSuccess = response?.success === true;
      const eventWebhookSuccess =
        response?.webhooks?.event_webhook?.success === true;
      const inboundParseSuccess =
        response?.webhooks?.inbound_parse?.success === true;

      if (outerSuccess && eventWebhookSuccess && inboundParseSuccess) {
        // Successfully connected/updated
        const successMessage = isConnected
          ? 'Email settings saved successfully!'
          : 'Email connected successfully!';
        toast.success(successMessage);

        // Clear API key field (never store it)
        form.setValue('api_key', '');

        // Reload user data to get updated sendgrid_integration_details
        window.location.reload();
      } else {
        // Partial success or failure - show error messages
        const errorMessages = [];

        if (!outerSuccess) {
          errorMessages.push('Failed to connect to SendGrid');
        }

        if (!eventWebhookSuccess) {
          const message =
            response?.webhooks?.event_webhook?.message ||
            'Event webhook configuration failed';
          errorMessages.push(`Event Webhook: ${message}`);
        }

        if (!inboundParseSuccess) {
          const message =
            response?.webhooks?.inbound_parse?.message ||
            'Inbound parse configuration failed';
          errorMessages.push(`Inbound Parse: ${message}`);
        }

        const errorMessage = errorMessages.join('. ');
        setError(errorMessage);
        toast.error(errorMessage);

        // Show DNS requirement if present
        if (response?.dns_required) {
          toast.info(response.dns_required, { duration: 10000 });
        }
      }
    } catch (error) {
      console.error('Failed to connect Email:', error);

      let errorMessage = 'Failed to connect Email. Please try again.';

      // Handle 422 errors (validation errors)
      if (error?.response?.status === 422) {
        const details = error?.response?.data?.details;
        const errorMsg = error?.response?.data?.error;
        // Show either details or error, prioritizing details if available
        errorMessage = details || errorMsg || 'Invalid SendGrid API key';
      } else if (error?.response?.data) {
        // Handle other API errors with response data
        errorMessage =
          error.response.data.error ||
          error.response.data.message ||
          errorMessage;
      } else if (error?.message) {
        // Handle error with message property
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2Icon className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='w-full max-w-2xl space-y-6'>
      {/* Connection Status Card */}
      {isConnected && sendgridDetails && (
        <div className='rounded-lg border bg-card p-6'>
          <div className='flex items-start gap-3'>
            <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary/10'>
              <Mail className='h-4 w-4 text-primary' />
            </div>
            <div className='flex-1 space-y-2'>
              <div className='flex items-center gap-2'>
                <h3 className='text-lg font-semibold'>Email Connected</h3>
                <span className='rounded-full bg-green-500/10 px-2 py-1 text-xs font-medium text-green-600 dark:text-green-400'>
                  {sendgridDetails.connection_status || 'Connected'}
                </span>
              </div>
              <div className='space-y-1 text-sm text-muted-foreground'>
                <div>
                  <span className='font-medium'>From Email:</span>{' '}
                  {sendgridDetails.from_email}
                </div>
                {sendgridDetails.from_name && (
                  <div>
                    <span className='font-medium'>From Name:</span>{' '}
                    {sendgridDetails.from_name}
                  </div>
                )}
                {sendgridDetails.reply_to_email && (
                  <div>
                    <span className='font-medium'>Reply To Email:</span>{' '}
                    {sendgridDetails.reply_to_email}
                  </div>
                )}
                {sendgridDetails.inbound_domain && (
                  <div>
                    <span className='font-medium'>Inbound Domain:</span>{' '}
                    {sendgridDetails.inbound_domain}
                  </div>
                )}
                {sendgridDetails.webhook_configured !== undefined && (
                  <div>
                    <span className='font-medium'>Webhook Configured:</span>{' '}
                    {sendgridDetails.webhook_configured ? 'Yes' : 'No'}
                  </div>
                )}
                {sendgridDetails.connected_at && (
                  <div>
                    <span className='font-medium'>Connected:</span>{' '}
                    {new Date(
                      sendgridDetails.connected_at
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className='rounded-lg border bg-card p-6'>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
            <div className='space-y-4'>
              <FormField
                control={form.control}
                name='api_key'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      SendGrid API Key
                      {!isConnected && <span className='text-red-500'> *</span>}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder='SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
                        disabled={connecting}
                        {...field}
                      />
                    </FormControl>
                    {isConnected && (
                      <FormDescription>
                        Leave blank to keep existing API key
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='inbound_domain'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Inbound Domain <span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='text'
                        placeholder='example.com'
                        disabled={connecting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='from_email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      From Email <span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        placeholder='sender@example.com'
                        disabled={connecting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='from_name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      From Name <span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='text'
                        placeholder='Your Name'
                        disabled={connecting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='reply_to_email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Reply To Email <span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        placeholder='reply@example.com'
                        disabled={connecting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {error && (
              <div className='rounded-md bg-destructive/10 p-4'>
                <p className='text-sm text-destructive'>{error}</p>
              </div>
            )}

            <div className='flex justify-end'>
              <Button
                type='submit'
                disabled={connecting}
                className='flex items-center justify-center'
              >
                {connecting ? (
                  <>
                    <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                    {isConnected ? 'Saving...' : 'Connecting...'}
                  </>
                ) : isConnected ? (
                  'Save Changes'
                ) : (
                  'Connect Email'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
